import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';
import { formatMoney } from '@/lib/format';
import { friendlyError } from '@/lib/errors';
import type { CartItem, LinePricing, ManualDiscount } from '@/lib/types';

const round2 = (n: number) => Math.round(n * 100) / 100;

const QUICK_PERCENTAGES = [5, 10, 15, 20, 25, 30];

interface DiscountModalProps {
  open: boolean;
  item: CartItem | null;
  pricing: LinePricing | null;
  currencySymbol: string;
  percentageAllowed: boolean;
  fixedAllowed: boolean;
  maxPercentage: number;
  isAdmin: boolean;
  authorizerName: string | null;
  onClose: () => void;
  onApply: (discount: ManualDiscount | null) => void;
}

export default function DiscountModal({
  open,
  item,
  pricing,
  currencySymbol,
  percentageAllowed,
  fixedAllowed,
  maxPercentage,
  isAdmin,
  authorizerName,
  onClose,
  onApply,
}: DiscountModalProps) {
  const existing = item?.manualDiscount ?? null;

  const [type, setType] = useState<'percent' | 'fixed'>('percent');
  const [value, setValue] = useState('');

  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authorizing, setAuthorizing] = useState(false);
  const [authorizedByName, setAuthorizedByName] = useState<string | null>(null);
  const [authorizedById, setAuthorizedById] = useState<string | null>(null);

  // Reinicia el estado al abrir con un artículo distinto.
  useEffect(() => {
    if (!open) return;
    const initialType = existing?.type ?? (percentageAllowed ? 'percent' : 'fixed');
    setType(initialType);
    setValue(existing ? String(existing.value) : '');
    setAuthEmail('');
    setAuthPassword('');
    setAuthError(null);
    setAuthorizing(false);
    setAuthorizedByName(existing?.authorizedByName ?? null);
    setAuthorizedById(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.product.id]);

  const unitPrice = pricing?.unitPrice ?? item?.product.sale_price ?? 0;
  const qty = item?.quantity ?? 0;
  const lineBase = round2(unitPrice * qty);
  const autoDiscount = pricing?.autoDiscount ?? 0;
  const hasAuto = autoDiscount > 0;

  const numVal = parseFloat(value) || 0;

  const manualAmount = useMemo(() => {
    if (numVal <= 0) return 0;
    if (type === 'percent') return round2((unitPrice * qty * numVal) / 100);
    return round2(numVal);
  }, [numVal, type, unitPrice, qty]);

  const effectivePct = useMemo(() => {
    if (numVal <= 0) return 0;
    if (type === 'percent') return round2(numVal);
    return lineBase > 0 ? round2((numVal / lineBase) * 100) : 0;
  }, [numVal, type, lineBase]);

  const totalDiscount = Math.min(round2(autoDiscount + manualAmount), lineBase);
  const finalLine = round2(Math.max(0, lineBase - totalDiscount));
  const finalUnit = qty > 0 ? round2(finalLine / qty) : 0;

  const invalidReason = (() => {
    if (numVal <= 0) return 'Introduce un descuento mayor que cero.';
    if (type === 'percent' && numVal > 100) return 'El porcentaje no puede pasar de 100%.';
    if (type === 'fixed' && numVal > lineBase) return 'El descuento no puede superar el precio del artículo.';
    if (finalLine < 0) return 'El descuento no puede dejar el precio en negativo.';
    return null;
  })();

  const requiresAuth = !isAdmin && maxPercentage > 0 && effectivePct > maxPercentage;
  const isAuthorized = !!authorizedByName;
  const canApply = !invalidReason && (!requiresAuth || isAuthorized);

  const handleAuthorize = async () => {
    setAuthError(null);
    if (!authEmail.trim() || !authPassword) {
      setAuthError('Ingresa el correo y la contraseña del administrador.');
      return;
    }
    setAuthorizing(true);
    try {
      const { data, error } = await supabase.functions.invoke('authorize_discount', {
        body: { email: authEmail.trim(), password: authPassword },
      });
      if (error) {
        let msg = error.message;
        const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
        if (ctx?.json) {
          try {
            const j = await ctx.json();
            if (j?.error) msg = j.error;
          } catch {
            /* ignora */
          }
        }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error as string);
      setAuthorizedByName(String(data.authorizer_name ?? 'Administrador'));
      setAuthorizedById(String(data.authorizer_id ?? ''));
      setAuthPassword('');
    } catch (err) {
      setAuthError(friendlyError(err, 'No se pudo autorizar el descuento.'));
    } finally {
      setAuthorizing(false);
    }
  };

  const handleApply = () => {
    if (!canApply) return;
    onApply({
      type,
      value: numVal,
      appliedByName: authorizerName,
      authorizedByName: isAuthorized ? authorizedByName : null,
    });
    onClose();
  };

  const handleRemove = () => {
    onApply(null);
    onClose();
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={existing ? 'Editar descuento extra' : 'Aplicar descuento extra'}
    >
      {item && (
        <div className="space-y-4">
          {/* Producto */}
          <div className="rounded-md border border-background-200 bg-background-100 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-foreground-500">Producto</p>
            <p className="mt-0.5 text-sm font-semibold text-foreground-950">{item.product.name}</p>
            <div className="mt-2 flex items-center justify-between text-sm text-foreground-600">
              <span>
                Precio unitario:{' '}
                <span className="font-medium text-foreground-900">
                  {formatMoney(unitPrice, currencySymbol)}
                </span>
              </span>
              <span>
                Cantidad: <span className="font-medium text-foreground-900">{qty}</span>
              </span>
            </div>
            {hasAuto && (
              <p className="mt-1.5 text-xs text-secondary-900">
                Ya tiene un descuento automático de {formatMoney(autoDiscount, currencySymbol)}; el
                descuento manual se sumará encima.
              </p>
            )}
          </div>

          {/* Tipo */}
          {(percentageAllowed || fixedAllowed) && (
            <div>
              <p className="mb-2 text-sm font-medium text-foreground-800">Tipo de descuento</p>
              <div className="grid grid-cols-2 gap-2">
                {percentageAllowed && (
                  <button
                    type="button"
                    onClick={() => {
                      setType('percent');
                      setValue('');
                      setAuthorizedByName(existing?.authorizedByName ?? null);
                    }}
                    className={`rounded-md border px-3 py-2.5 text-sm font-medium transition cursor-pointer ${
                      type === 'percent'
                        ? 'border-primary-400 bg-primary-50 text-primary-800'
                        : 'border-background-300 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    <i className="ri-percent-line mr-1.5" />
                    Porcentaje
                  </button>
                )}
                {fixedAllowed && (
                  <button
                    type="button"
                    onClick={() => {
                      setType('fixed');
                      setValue('');
                      setAuthorizedByName(existing?.authorizedByName ?? null);
                    }}
                    className={`rounded-md border px-3 py-2.5 text-sm font-medium transition cursor-pointer ${
                      type === 'fixed'
                        ? 'border-primary-400 bg-primary-50 text-primary-800'
                        : 'border-background-300 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    <i className="ri-money-dollar-circle-line mr-1.5" />
                    Monto fijo
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Valor */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">
              {type === 'percent' ? 'Porcentaje' : `Monto (${currencySymbol})`}
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                step={type === 'percent' ? '1' : '0.01'}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setAuthorizedByName(existing?.authorizedByName ?? null);
                }}
                autoFocus
                className={inputClass}
                placeholder={type === 'percent' ? '10' : '150'}
              />
              {type === 'percent' && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500">
                  %
                </span>
              )}
            </div>
            {type === 'percent' && (
              <div className="mt-2 flex flex-wrap gap-2">
                {QUICK_PERCENTAGES.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => {
                      setValue(String(p));
                      setAuthorizedByName(existing?.authorizedByName ?? null);
                    }}
                    className={`min-w-[48px] rounded-full border px-3 py-1 text-xs font-semibold transition cursor-pointer ${
                      numVal === p
                        ? 'border-primary-400 bg-primary-500 text-background-50'
                        : 'border-background-300 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {p}%
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Previsualización */}
          <div className="rounded-md border border-background-200 bg-background-50 p-4 text-sm">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-foreground-500">
              Previsualización
            </p>
            <div className="space-y-1.5">
              <div className="flex justify-between">
                <span className="text-foreground-600">Precio unitario</span>
                <span className="font-medium text-foreground-900">
                  {formatMoney(unitPrice, currencySymbol)}
                </span>
              </div>
              {hasAuto && (
                <div className="flex justify-between">
                  <span className="text-foreground-600">Descuento automático</span>
                  <span className="font-medium text-secondary-900">
                    -{formatMoney(autoDiscount, currencySymbol)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-foreground-600">
                  Descuento extra{type === 'percent' && numVal > 0 ? ` ${numVal}%` : ''}
                </span>
                <span className="font-medium text-accent-700">
                  -{formatMoney(manualAmount, currencySymbol)}
                </span>
              </div>
              <div className="flex justify-between border-t border-background-200 pt-1.5">
                <span className="text-foreground-600">Precio final unitario</span>
                <span className="font-medium text-foreground-900">
                  {formatMoney(finalUnit, currencySymbol)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-foreground-950">Total del artículo</span>
                <span className="font-heading text-base font-bold text-foreground-950">
                  {formatMoney(finalLine, currencySymbol)}
                </span>
              </div>
            </div>
          </div>

          {/* Autorización */}
          {requiresAuth && (
            <div className="rounded-md border border-accent-300 bg-accent-50 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-accent-900">
                <i className="ri-shield-keyhole-line" />
                {isAuthorized
                  ? `Autorizado por ${authorizedByName}`
                  : 'Este descuento requiere autorización.'}
              </p>
              {!isAuthorized && (
                <>
                  <p className="mt-1 text-xs text-foreground-600">
                    El descuento ({effectivePct}%) supera el máximo permitido ({maxPercentage}%).
                    Un administrador debe aprobarlo con su correo y contraseña.
                  </p>
                  <div className="mt-3 space-y-2">
                    <input
                      type="email"
                      value={authEmail}
                      onChange={(e) => setAuthEmail(e.target.value)}
                      placeholder="Correo del administrador"
                      autoComplete="off"
                      className={inputClass}
                    />
                    <input
                      type="password"
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      placeholder="Contraseña del administrador"
                      autoComplete="new-password"
                      className={inputClass}
                    />
                    {authError && <p className="text-xs text-accent-700">{authError}</p>}
                    <button
                      type="button"
                      onClick={handleAuthorize}
                      disabled={authorizing}
                      className="w-full rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-accent-600 disabled:opacity-60 cursor-pointer"
                    >
                      {authorizing ? 'Verificando...' : 'Autorizar descuento'}
                    </button>
                  </div>
                </>
              )}
              {isAuthorized && authorizedById && (
                <p className="mt-1 text-xs text-foreground-500">
                  Aprobación registrada para esta venta.
                </p>
              )}
            </div>
          )}

          {invalidReason && numVal > 0 && (
            <p className="text-xs text-accent-700">{invalidReason}</p>
          )}

          {/* Acciones */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {existing && (
              <button
                type="button"
                onClick={handleRemove}
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-accent-300 px-4 py-2.5 text-sm font-medium text-accent-700 transition hover:bg-accent-50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-delete-bin-6-line" /> Eliminar descuento
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition hover:bg-background-100 whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!canApply}
              className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {existing ? 'Guardar descuento' : 'Aplicar descuento'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}